import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAgencies, updateAgencyStatus } from '../../store/superuserSlice';
import { Alert, Skeleton, Switch, Button, Tooltip } from 'antd';
import { EditOutlined, BankOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import toast from 'react-hot-toast';
import ReusableTable from '../../Reuseable/ReusableTable/ReusableTable';
import PanelCard from '../DashboardPage/components/PanelCard';
import AgencyDetailsModal from './AgencyDetailsModal';
import './AgenciesPage.scss';

dayjs.extend(customParseFormat);

const EMPTY = '—';

const StaticSkeleton = () => <Skeleton.Input size="small" active />;

const hasValue = (value) =>
  value !== null && value !== undefined && value !== '';

const toDay = (value) => {
  if (!hasValue(value)) return null;
  const direct = dayjs(value);
  if (direct.isValid()) return direct;
  const loose = dayjs(value, ['YYYY-MM-DD', 'DD/MM/YYYY', 'MMM D, YYYY']);
  return loose.isValid() ? loose : null;
};

const formatDate = (value) => {
  const day = toDay(value);
  if (day) return day.format('DD MMM YYYY');
  return hasValue(value) ? String(value) : null;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/* "412 days left" is a number you have to convert in your head, but rounding
   it to "1 year" throws away the part you actually need when a renewal is
   close. The span is broken down and every non-zero part is reported, so 65
   days reads as "2 months 4 days".

   The breakdown walks the calendar with dayjs rather than dividing by 30 and
   365, so it uses the real length of the months being crossed: 65 days from
   9 Sep spans September (30) and October (31), which is 2 months and 4 days -
   naive 30-day math would have said 2 months 5 days. */
const humanSpan = (start, end) => {
  const years = end.diff(start, 'year');
  const afterYears = start.add(years, 'year');
  const months = end.diff(afterYears, 'month');
  const afterMonths = afterYears.add(months, 'month');
  const days = end.diff(afterMonths, 'day');

  const parts = [];

  if (years > 0) parts.push(plural(years, 'year'));
  if (months > 0) parts.push(plural(months, 'month'));

  if (years === 0 && months === 0) {
    /* under a month, weeks read better than a two-digit day count */
    const weeks = Math.floor(days / 7);
    const spareDays = days - weeks * 7;
    if (weeks > 0) parts.push(plural(weeks, 'week'));
    if (spareDays > 0 || parts.length === 0) parts.push(plural(spareDays, 'day'));
  } else if (days > 0) {
    parts.push(plural(days, 'day'));
  }

  return parts.join(' ');
};

const validityOf = (record) => {
  const from = toDay(record?.from_date);
  const to = toDay(record?.to_date);
  if (!to) return null;

  const today = dayjs().startOf('day');
  const end = to.startOf('day');

  /* a contract that has not started yet has no "days left" to report */
  if (from && from.startOf('day').isAfter(today)) {
    const start = from.startOf('day');
    return {
      band: 'is-pending',
      text:
        start.diff(today, 'day') === 1
          ? 'Starts tomorrow'
          : `Starts in ${humanSpan(today, start)}`,
    };
  }

  /* the banding stays on the raw day count - only the wording is rounded */
  const days = end.diff(today, 'day');
  if (days < 0) {
    return {
      band: 'is-expired',
      text: `Expired ${humanSpan(end, today)} ago`,
    };
  }
  if (days === 0) return { band: 'is-expired', text: 'Expires today' };

  return {
    band: days <= 30 ? 'is-soon' : 'is-ok',
    text: `${humanSpan(today, end)} left`,
  };
};

const StatusSwitch = ({ record }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  return (
    <Switch
      className="agencyStatusSwitch"
      checked={record.active}
      loading={loading}
      onChange={async (checked) => {
        setLoading(true);
        try {
          await dispatch(updateAgencyStatus({ id: record.id, active: checked })).unwrap();
          toast.success('Status updated successfully');

        } catch (err) {
          toast.error('Failed to update status');
        } finally {
          setLoading(false);
        }
      }}
    />
  );
};

const AgenciesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { agencies, agencies_loading, agencies_error } = useSelector(
    (state) => state.superuser
  );
  const loading = agencies_loading;

  const [params, setParams] = useState({ page: 1, page_size: 10 });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);

  useEffect(() => {
    dispatch(fetchAgencies(params));
  }, [dispatch, params]);

  const rows = useMemo(() => agencies?.results || [], [agencies]);

  const skeletonRows = useMemo(
    () => Array(5).fill(0).map((_, i) => ({ id: `skeleton-${i}` })),
    []
  );

  const handlePaginationChange = (page, page_size) => {
    setParams({ page, page_size });
  };

  const columns = [
    {
      title: 'Agency',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text) =>
        loading ? (
          <StaticSkeleton />
        ) : (
          <span className="agencyName">
            <span className="agencyName__avatar" aria-hidden="true">
              {text?.charAt(0)?.toUpperCase() || 'A'}
            </span>
            <span className="agencyName__text">{text || EMPTY}</span>
          </span>
        ),
    },
    {
      title: 'Owner',
      key: 'owner',
      render: (_, record) => {
        if (loading) return <StaticSkeleton />;
        const name = [record?.owner?.first_name, record?.owner?.last_name]
          .filter(Boolean)
          .join(' ');
        return name ? (
          <span className="agencyText">{name}</span>
        ) : (
          <span className="agencyEmpty">{EMPTY}</span>
        );
      },
    },
    {
      title: 'Agents',
      key: 'agents',
      align: 'center',
      width: 150,
      /* The card gave Limit / Used / Left three tiles of their own; as a
         column they read better as the ratio plus what is left of it. */
      render: (_, record) => {
        if (loading) return <StaticSkeleton />;
        const limit = record?.team_summary?.agent_limit;
        const used = record?.team_summary?.agents_used;
        const remaining = record?.team_summary?.agents_remaining;

        if (!hasValue(limit) && !hasValue(used)) {
          return <span className="agencyEmpty">{EMPTY}</span>;
        }

        return (
          <span className="agencyAgents">
            <span className="agencyAgents__ratio">
              {hasValue(used) ? used : EMPTY}
              <i>/ {hasValue(limit) ? limit : EMPTY}</i>
            </span>
            {hasValue(remaining) && (
              <span
                className={`agencyAgents__left ${
                  Number(remaining) > 0 ? 'is-some' : 'is-none'
                }`}
              >
                {Number(remaining) > 0 ? `${remaining} left` : 'full'}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: 'Validity',
      key: 'validity',
      align: 'center',
      width: 150,
      render: (_, record) => {
        if (loading) return <StaticSkeleton />;

        const from = formatDate(record?.from_date);
        const to = formatDate(record?.to_date);
        const validity = validityOf(record);

        if (!validity) {
          /* no usable end date - show whatever the record does carry */
          return to ? (
            <span className="agencyText">{to}</span>
          ) : (
            <span className="agencyEmpty">{EMPTY}</span>
          );
        }

        return (
          <Tooltip title={`${from || EMPTY} to ${to || EMPTY}`}>
            <span className={`agencyValidity ${validity.band}`}>
              {validity.text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      align: 'center',
      width: 110,
      render: (_, record) =>
        loading ? <StaticSkeleton /> : <StatusSwitch record={record} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 160,
      render: (_, record) => {
        if (loading) return <StaticSkeleton />;
        return (
          <div className="agencyActions">
            <Tooltip title="View agency details">
              <Button
                className="agencyBtn"
                onClick={() => {
                  setSelectedAgency(record);
                  setModalOpen(true);
                }}
              >
                View Details
              </Button>
            </Tooltip>
            <Tooltip title="Edit agency">
              <Button
                className="agencyIconBtn is-edit"
                icon={<EditOutlined />}
                onClick={() => navigate(`/agencies/edit/${record.id}`, { state: record })}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const isEmpty = !loading && rows.length === 0;

  return (
    <div className="agenciesPageMain">
      {agencies_error && (
        <Alert
          message={agencies_error}
          type="error"
          showIcon
          className="agenciesError"
        />
      )}

      {/* Flush body + an inner `*-report-table` wrapper is how the dashboard
          panels carry a table, so the rows sit at the same inset here. */}
      <PanelCard
        title="Manage Agencies"
        subtitle={
          loading
            ? 'Loading agencies…'
            : `${agencies?.count ?? rows.length} ${
                (agencies?.count ?? rows.length) === 1 ? 'agency' : 'agencies'
              }`
        }
        icon={<BankOutlined />}
        accent="brand"
        padded={false}
        actions={
          <Button
            className="createAgencyBtn"
            icon={<PlusOutlined />}
            onClick={() => navigate('/agencies/create')}
          >
            Create Agency
          </Button>
        }
      >
        {isEmpty ? (
          <div className="agenciesEmpty">
            <span className="agenciesEmpty__icon">
              <BankOutlined />
            </span>
            <h4>No agencies yet</h4>
            <p>Agencies you create will be listed here.</p>
            <Button
              className="createAgencyBtn"
              icon={<PlusOutlined />}
              onClick={() => navigate('/agencies/create')}
            >
              Create Agency
            </Button>
          </div>
        ) : (
          <div className="agencies-report-table">
            <ReusableTable
              columns={columns}
              dataSource={loading ? skeletonRows : rows}
              totalItems={agencies?.count || 0}
              rowKey="id"
              CurrentPageNo={params.page}
              pageSize={params.page_size}
              triggerChange={handlePaginationChange}
            />
          </div>
        )}
      </PanelCard>

      <AgencyDetailsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedAgency(null);
        }}
        data={selectedAgency}
      />
    </div>
  );
};

export default AgenciesPage;
